import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Leaf, Heart, Shield, Droplets } from 'lucide-react';
import Logo from './Logo';

const Footer = () => {
  return (
    <footer className="bg-brand-charcoal text-white" role="contentinfo" aria-label="Site footer">
      {/* Trust indicators strip */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Leaf className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider text-gray-400">Natural Ingredients</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Heart className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider text-gray-400">Cruelty Free</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider text-gray-400">Made in UK</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Droplets className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-wider text-gray-400">Sensitive Skin Safe</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About with Logo */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <Link to="/" className="inline-block">
                <span className="font-serif text-2xl text-white tracking-wide">MIREVY</span>
              </Link>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Luxury botanical skincare, made in the UK with nature's purest ingredients. 
              Nurturing sensitive skin with gentle, effective formulas.
            </p>
            <address className="not-italic space-y-3 text-sm text-gray-400">
              <div className="flex items-center space-x-3">
                <Mail size={14} className="text-brand-gold" />
                <a href="mailto:Mmaary.2007@gmail.com" className="hover:text-brand-gold transition-colors duration-300">
                  Mmaary.2007@gmail.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={14} className="text-brand-gold" />
                <a href="tel:+447388071719" className="hover:text-brand-gold transition-colors duration-300">
                  +44 7388 071719
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin size={14} className="text-brand-gold" />
                <span>Edgware, London, UK</span>
              </div>
            </address>
          </div>

          {/* Shop */}
          <nav aria-label="Shop categories">
            <h2 className="text-xs uppercase tracking-luxury text-brand-gold mb-6 font-medium">Shop</h2>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <Link to="/shop/skincare" className="hover:text-white transition-colors duration-300">
                  Skincare
                </Link>
              </li>
              <li>
                <Link to="/shop/haircare" className="hover:text-white transition-colors duration-300">
                  Haircare
                </Link>
              </li>
              <li>
                <Link to="/shop/body" className="hover:text-white transition-colors duration-300">
                  Body Care
                </Link>
              </li>
              <li>
                <Link to="/shop?filter=bestseller" className="hover:text-white transition-colors duration-300">
                  Bestsellers
                </Link>
              </li>
              <li>
                <Link to="/shop?filter=new" className="hover:text-white transition-colors duration-300">
                  New Arrivals
                </Link>
              </li>
              <li>
                <Link to="/bundles" className="hover:text-white transition-colors duration-300">
                  Gift Sets
                </Link>
              </li>
            </ul>
          </nav>

          {/* Customer Service */}
          <nav aria-label="Customer service">
            <h2 className="text-xs uppercase tracking-luxury text-brand-gold mb-6 font-medium">Help</h2>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <Link to="/contact" className="hover:text-white transition-colors duration-300">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-white transition-colors duration-300">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/delivery-policy" className="hover:text-white transition-colors duration-300">
                  Shipping & Delivery
                </Link>
              </li>
              <li>
                <Link to="/returns-policy" className="hover:text-white transition-colors duration-300">
                  Returns & Exchanges
                </Link>
              </li>
              <li>
                <Link to="/track-order" className="hover:text-white transition-colors duration-300">
                  Track Order
                </Link>
              </li>
              <li>
                <Link to="/account" className="hover:text-white transition-colors duration-300">
                  My Account
                </Link>
              </li>
            </ul>
          </nav>

          {/* About & Legal */}
          <nav aria-label="About and legal">
            <h2 className="text-xs uppercase tracking-luxury text-brand-gold mb-6 font-medium">About</h2>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <Link to="/about" className="hover:text-white transition-colors duration-300">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors duration-300">
                  Journal
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="hover:text-white transition-colors duration-300">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="hover:text-white transition-colors duration-300">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/hygiene-policy" className="hover:text-white transition-colors duration-300">
                  Hygiene Policy
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Mirevy. All rights reserved.</p>
            <p>Made Botanical Skincare | Edgware, London, UK</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
